import { ServiceTemplateService } from '@services';
import { useCallback, useEffect, useState } from 'react';

const byId = (a: number, b: number) => a - b;

const sameSet = (a: number[], b: number[]) => {
  if (a.length !== b.length) return false;

  const left = [...a].sort(byId);
  const right = [...b].sort(byId);

  return left.every((id, index) => id === right[index]);
};

/**
 * Templates a service belongs to, together with the working copy the form edits.
 * The loaded set is kept alongside the selection so the caller can skip the save call when the
 * user did not actually change anything.
 */
export const useServiceTemplatesForService = (serviceId?: number) => {
  const [assigned, setAssigned] = useState<number[]>([]);
  const [selected, setSelected] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!serviceId) {
      setAssigned([]);
      setSelected([]);
      return;
    }

    // Guards against a late response from a previous service overwriting the current one.
    let active = true;
    setLoading(true);

    ServiceTemplateService.getByService(serviceId)
      .then((templates) => {
        if (!active) return;

        const ids = templates.map((x) => x.id);
        setAssigned(ids);
        setSelected(ids);
      })
      .catch((error) => console.error(error))
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [serviceId]);

  const reset = useCallback(() => {
    setAssigned([]);
    setSelected([]);
  }, []);

  return {
    loading,
    templateIds: selected,
    setTemplateIds: setSelected,
    hasChanges: !sameSet(assigned, selected),
    reset,
  };
};
